import React, { createContext, useContext, useState, useEffect } from 'react';

const ActivityContext = createContext();

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

export const ActivityProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [bannedIPs, setBannedIPs] = useState(new Set());

  // Cleanup old sessions periodically
  React.useEffect(() => {
    const cleanup = () => {
      const now = new Date();
      setSessions(prev => prev.filter(session => {
        if (session.status !== 'active') return true;
        
        const lastActivity = new Date(session.lastActivity);
        const timeDiff = now - lastActivity;
        const thirtyMinutes = 30 * 60 * 1000;
        
        // Mark sessions as ended if inactive for 30 minutes
        if (timeDiff > thirtyMinutes) {
          return false;
        }
        return true;
      }));
    };

    const interval = setInterval(cleanup, 5 * 60 * 1000); // Run every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Generate session ID
  const generateSessionId = () => {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  // Get user's IP and location
  const getUserInfo = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return {
        ip: data.ip || 'Unknown',
        location: `${data.city || 'Unknown'}, ${data.country_name || 'Unknown'}`,
        isp: data.org || 'Unknown ISP',
        timezone: data.timezone || 'Unknown'
      };
    } catch {
      return {
        ip: 'Unknown',
        location: 'Unknown',
        isp: 'Unknown ISP',
        timezone: 'Unknown'
      };
    }
  };

  // Initialize session
  const initializeSession = async () => {
    // Check if session already exists
    if (currentSession && currentSession.status === 'active') {
      return currentSession.id;
    }
    
    const userInfo = await getUserInfo();
    const sessionId = generateSessionId();
    
    const session = {
      id: sessionId,
      ...userInfo,
      loginTime: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      toolsUsed: [],
      actionsCount: 0,
      status: 'active'
    };

    setCurrentSession(session);
    setSessions(prev => {
      // Remove any existing active sessions for this IP to prevent duplicates
      const filtered = prev.filter(s => s.ip !== userInfo.ip || s.status !== 'active');
      return [...filtered, session];
    });
    
    logActivity('SESSION_START', 'User logged in', { sessionId, ...userInfo });
    
    return sessionId;
  };

  // Update session activity
  const updateActivity = (toolName, action, details = {}) => {
    if (!currentSession) return;

    const now = new Date().toISOString();
    
    setCurrentSession(prev => ({
      ...prev,
      lastActivity: now,
      actionsCount: prev.actionsCount + 1,
      toolsUsed: prev.toolsUsed.includes(toolName) 
        ? prev.toolsUsed 
        : [...prev.toolsUsed, toolName]
    }));

    setSessions(prev => prev.map(session => 
      session.id === currentSession.id 
        ? {
            ...session,
            lastActivity: now,
            actionsCount: session.actionsCount + 1,
            toolsUsed: session.toolsUsed.includes(toolName) 
              ? session.toolsUsed 
              : [...session.toolsUsed, toolName]
          }
        : session
    ));

    logActivity('TOOL_USAGE', `Used ${toolName}: ${action}`, {
      sessionId: currentSession.id,
      toolName,
      action,
      ...details
    });
  };

  // Log activity
  const logActivity = (type, message, details = {}) => {
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      type,
      message,
      sessionId: currentSession?.id || 'unknown',
      ip: currentSession?.ip || 'unknown',
      ...details
    };

    setActivityLog(prev => [logEntry, ...prev].slice(0, 1000)); // Keep last 1000 entries
  };

  // End session
  const endSession = () => {
    if (currentSession) {
      logActivity('SESSION_END', 'User logged out', { 
        sessionId: currentSession.id,
        duration: Date.now() - new Date(currentSession.loginTime).getTime()
      });
      
      setSessions(prev => prev.map(session => 
        session.id === currentSession.id 
          ? { ...session, status: 'ended', logoutTime: new Date().toISOString() }
          : session
      ));
      
      setCurrentSession(null);
    }
  };

  // Admin functions
  const kickSession = (sessionId) => {
    setSessions(prev => prev.map(session => 
      session.id === sessionId 
        ? { ...session, status: 'kicked', kickedAt: new Date().toISOString() }
        : session
    ));
    
    logActivity('ADMIN_ACTION', `Session ${sessionId} was kicked by admin`, { 
      targetSessionId: sessionId,
      adminAction: 'kick'
    });

    // If it's the current session, end it
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
  };

  const banIP = (ip) => {
    setBannedIPs(prev => new Set([...prev, ip]));
    
    // Kick all sessions from this IP
    setSessions(prev => prev.map(session => 
      session.ip === ip && session.status === 'active'
        ? { ...session, status: 'banned', bannedAt: new Date().toISOString() }
        : session
    ));
    
    logActivity('ADMIN_ACTION', `IP ${ip} was banned by admin`, { 
      targetIP: ip,
      adminAction: 'ban'
    });

    // If current session is from banned IP, end it
    if (currentSession?.ip === ip) {
      setCurrentSession(null);
    }
  };

  const unbanIP = (ip) => {
    setBannedIPs(prev => {
      const newSet = new Set(prev);
      newSet.delete(ip);
      return newSet;
    });
    
    logActivity('ADMIN_ACTION', `IP ${ip} was unbanned by admin`, { 
      targetIP: ip,
      adminAction: 'unban'
    });
  };

  // Check if IP is banned
  const isIPBanned = (ip) => {
    return bannedIPs.has(ip);
  };

  // Get statistics
  const getStats = () => {
    const activeSessions = sessions.filter(s => s.status === 'active');
    const totalSessions = sessions.length;
    const uniqueIPs = new Set(sessions.map(s => s.ip)).size;
    const mostUsedTools = {};
    
    activityLog
      .filter(log => log.type === 'TOOL_USAGE')
      .forEach(log => {
        mostUsedTools[log.toolName] = (mostUsedTools[log.toolName] || 0) + 1;
      });

    return {
      activeSessions: activeSessions.length,
      totalSessions,
      uniqueIPs,
      bannedIPs: bannedIPs.size,
      totalActions: activityLog.filter(log => log.type === 'TOOL_USAGE').length,
      mostUsedTools: Object.entries(mostUsedTools)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
    };
  };

  const value = {
    sessions,
    currentSession,
    activityLog,
    bannedIPs: Array.from(bannedIPs),
    initializeSession,
    updateActivity,
    endSession,
    kickSession,
    banIP,
    unbanIP,
    isIPBanned,
    getStats
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export default ActivityContext;
